import {
  Badge,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { CheckIcon, Loader2, LogInIcon, UserCheckIcon } from "lucide-react";
import { isMultiMemberPackageKind } from "@/modules/private-lessons/domain/lib/package-kind";

type CheckInHubTableProps = {
  data: CheckInListItem[];
  isLoading?: boolean;
  onCheckIn: (item: CheckInListItem) => void;
  onCompleteSession?: (item: CheckInListItem) => void;
  completingSessionId?: string | null;
};

function timeToAMPM(time: string): string {
  const parts = time.split(":").map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const hour = h % 12 || 12;
  const ampm = h < 12 ? "AM" : "PM";
  return `${hour.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function ActionCell({
  item,
  onCheckIn,
  onCompleteSession,
  completingSessionId,
}: {
  item: CheckInListItem;
  onCheckIn: (item: CheckInListItem) => void;
  onCompleteSession?: (item: CheckInListItem) => void;
  completingSessionId?: string | null;
}) {
  const isVerified =
    item.type === "general_class"
      ? item.status === "completed"
      : item.status === "completed";
  const isScheduled =
    item.type === "general_class"
      ? item.status === "scheduled"
      : item.status === "scheduled";

  if (item.status === "cancelled") {
    return <span className="text-muted-foreground">-</span>;
  }
  if (isVerified) {
    const detailLink =
      item.type === "general_class"
        ? {
            to: "/app/classes/sessions/$id" as const,
            params: { id: item.id },
          }
        : item.type === "private_lesson"
          ? {
              to: "/app/private-lessons/bookings/$id" as const,
              params: { id: item.id },
            }
          : null;

    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <CheckIcon className="h-4 w-4" />
          VERIFIED
        </span>
        {detailLink && (
          <Link
            to={detailLink.to}
            params={detailLink.params}
            className="text-primary text-xs hover:underline"
          >
            ແກ້ໄຂຄູສອນ
          </Link>
        )}
      </div>
    );
  }
  if (item.type === "general_class" && isScheduled) {
    const isCompleting = completingSessionId === item.id;
    const partnerOnly =
      item.engagementKind === "partner_instructor_only";
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="default"
          onClick={() => onCheckIn(item)}
          className="gap-1.5"
        >
          <LogInIcon className="h-4 w-4" />
          {partnerOnly ? "ກວດຊື່ຄູ" : "ກວດຊື່"}
        </Button>
        {onCompleteSession && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onCompleteSession(item)}
            disabled={isCompleting}
            className="gap-1.5"
          >
            {isCompleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckIcon className="h-4 w-4" />
            )}
            ສຳເລັດ
          </Button>
        )}
      </div>
    );
  }
  if (item.type === "private_lesson" && isScheduled) {
    const isGroup = isMultiMemberPackageKind(item.packageKind);
    const checked = item.memberAttendanceCount ?? 0;
    const total = item.groupSize ?? 0;
    return (
      <div className="flex flex-wrap items-center gap-2">
        {isGroup && total > 0 && (
          <span className="text-muted-foreground text-xs">
            {checked}/{total} ຄົນ
          </span>
        )}
        <Button
          size="sm"
          variant="default"
          onClick={() => onCheckIn(item)}
          className="gap-1.5"
        >
          <UserCheckIcon className="h-4 w-4" />
          {isGroup ? "ກວດຊື່ກຸ່ມ" : "ຢືນຢັນການເຂົ້າຮຽນ"}
        </Button>
        {isGroup && onCompleteSession && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onCompleteSession(item)}
            disabled={completingSessionId === item.id}
            className="gap-1.5"
          >
            {completingSessionId === item.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckIcon className="h-4 w-4" />
            )}
            ສຳເລັດ
          </Button>
        )}
      </div>
    );
  }
  if (item.type === "room_booking" && isScheduled) {
    const isCompleting = completingSessionId === item.id;
    return (
      onCompleteSession && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onCompleteSession(item)}
          disabled={isCompleting}
          className="gap-1.5"
        >
          {isCompleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckIcon className="h-4 w-4" />
          )}
          ສຳເລັດ
        </Button>
      )
    );
  }
  return <span className="text-muted-foreground">-</span>;
}

export function CheckInHubTable({
  data,
  isLoading,
  onCheckIn,
  onCompleteSession,
  completingSessionId,
}: CheckInHubTableProps) {
  if (isLoading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ເວລາ</TableHead>
            <TableHead>ປະເພດ</TableHead>
            <TableHead>ຄລາດສ໌ / ນັກຮຽນ</TableHead>
            <TableHead>ຫ້ອງ</TableHead>
            <TableHead>ຄົນສອນ</TableHead>
            <TableHead>ການກວດຊື່</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map((id) => (
            <TableRow key={id}>
              <TableCell>
                <Skeleton className="h-5 w-24" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-32" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-16" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-28" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-8 w-24" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ເວລາ</TableHead>
          <TableHead>ປະເພດ</TableHead>
          <TableHead>ຄລາດສ໌ / ນັກຮຽນ</TableHead>
          <TableHead>ຫ້ອງ</TableHead>
          <TableHead>ຄົນສອນ</TableHead>
          <TableHead>ການກວດຊື່</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={6}
              className="h-24 text-center text-muted-foreground"
            >
              ບໍ່ພົບລາຍການສຳລັບວັນທີເລືອກ
            </TableCell>
          </TableRow>
        ) : (
          data.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">
                {timeToAMPM(item.startTime)}
                {item.endTime ? ` - ${timeToAMPM(item.endTime)}` : ""}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge
                    variant={
                      item.type === "general_class" ? "secondary" : "outline"
                    }
                    className={
                      item.type === "general_class"
                        ? "bg-primary/10 text-primary"
                        : "bg-muted"
                    }
                  >
                    {item.type === "general_class"
                      ? "ຄລາສທົ່ວໄປ"
                      : item.type === "room_booking"
                        ? "ຈອງຫ້ອງ"
                        : "ວິຊາສ່ວນຕົວ"}
                  </Badge>
                  {item.type === "general_class" &&
                    item.engagementKind === "partner_instructor_only" && (
                      <Badge variant="outline" className="border-foreground/30">
                        Partner
                      </Badge>
                    )}
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <div className="font-medium">{item.subjectOrStudent}</div>
                  {item.subjectDetail && (
                    <div
                      title={item.subjectDetail}
                      className="max-w-[200px] truncate text-muted-foreground text-xs"
                    >
                      {item.subjectDetail}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>{item.roomName || "-"}</TableCell>
              <TableCell>
                {item.instructorNames?.length
                  ? item.instructorNames.join(", ")
                  : "-"}
              </TableCell>
              <TableCell>
                <ActionCell
                  item={item}
                  onCheckIn={onCheckIn}
                  onCompleteSession={onCompleteSession}
                  completingSessionId={completingSessionId}
                />
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
